import { Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { queryClient } from "@/shared/infrastructure/query/query-client";
import { initI18n } from "@/shared/infrastructure/i18n/i18n";
import { AuthProvider } from "@/modules/auth/infrastructure/react/auth-provider";
import { TermsAcceptanceModal } from "@/modules/terms/presentation/components/TermsAcceptanceModal";
import { Toaster } from "@/shared/ui/toaster";
import { PageLoader } from "@/shared/ui/page-loader";
import { ThemeProvider } from "./ThemeProvider";

function I18nGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    initI18n(queryClient).then(() => setReady(true));
  }, []);

  if (!ready) return <PageLoader />;
  return <>{children}</>;
}

function OptionalGoogleOAuthProvider({ children }: { children: ReactNode }) {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!googleClientId) {
    return <>{children}</>;
  }
  return <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nGate>
        <Suspense fallback={<PageLoader />}>
          <ThemeProvider>
            <OptionalGoogleOAuthProvider>
              <AuthProvider>
                {children}
                <TermsAcceptanceModal />
                <Toaster />
              </AuthProvider>
            </OptionalGoogleOAuthProvider>
          </ThemeProvider>
        </Suspense>
      </I18nGate>
    </QueryClientProvider>
  );
}
